import * as React from "react";
import * as _ from "lodash";
import { PanelMenuProps } from "@bizform/primereact/components/panelmenu/PanelMenu";
import { MenuItem } from "@bizform/primereact/components/menuitem/MenuItem";
import { Static } from "@bizform/type/decorator/Static.decorator";
import { BooleanByTrueFalse } from "@bizform/type/model/data_set/LookUp.model";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { AccessibilityMenu, ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { AccordionMenu } from "./BfAccordionMenu/AccordionMenu";
import { MenuControlHelper } from "./BfAccordionMenu/MenuControlHelper";
import "./BfAccordionMenu/AccordionMenu.css";
import { I18n } from "../../misc/model/I18n.model";


export const ITEMKEY = "_itemKey";

type ActiveItem = {
    [key: string]: boolean;
};

export type ItemInformation = {
    hasItemValue: boolean;
    hasItems: boolean;
};

type Props = PanelMenuProps & {
};

type WrappedCompState = {
    // attribute
    id?: string;
    className?: string;
    multiple: number;
    enableSearch: number;
    model: MenuItem[];
    filteredModel: MenuItem[];
    isSearched?: boolean;
    allExpand?: boolean;
    firstExpand?: boolean;
    firstExpanded?: boolean;
    allCollapse?: boolean;
    activeItems?: ActiveItem;
    lastSelectedItem?: ActiveItem;
    activeItemsBeforeSearch?: ActiveItem;
    placeholder?: string;
    /*
        selectedItemBeforeSearch는 activeItemsBeforeSearch와는 달리 구현 필요 없음.
        multiple인 경우 검색 이전의 선택 결과도 그대로 유지되어야 하고 검색 시에 선택된 결과도 유지되어야 한다.
     */

    // event
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
};

/**
 * Accordion Menu component element
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<null>>()
export class BfAccordionMenu extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_8NhmKJByLApxT7nEyiz_QXfEbEpNMax",
        category: ComponentCategory.MENU,
        label: I18n.componentElement.accordionMenu.label,
        description: I18n.componentElement.accordionMenu.description,
        icon: "",
        property: null
    };

    focus(): void {
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);
            newState.wrappedCompState.firstExpanded = false;

            const menuControlHelper = new MenuControlHelper(newState.wrappedCompState.model);
            menuControlHelper.markItemKey();
            newState.wrappedCompState.filteredModel = ObjectHelper.cloneDeep(newState.wrappedCompState.model);

            if (newState.wrappedCompState.allExpand) {
                newState.wrappedCompState.allExpand = true; /* can be received as 1 */
                newState.wrappedCompState.allCollapse = false;
            } else {
                /* if newState.wrappedCompState.allExpand is false or undefined */
                newState.wrappedCompState.allExpand = false;
                newState.wrappedCompState.allCollapse = true;
            }
            this._newModel = true;
            if (newState.wrappedCompState.isSearched) {
                this.setState(newState, this._searchItem);
            } else {
                this.setState(newState);
            }
        } catch (e) {
            Debug.console.error(e);
        }
    }

    private _itemValueEvent(e: {originalEvent: Event, item: MenuItem}): void {
        /*
         * 주의:
         * itemValue 는 MenuItem 에 정의된 값이 프로퍼티가 아니다.
         * property panel 에서 form state 변경을 위해 itemValue 를 컴포넌트에 설정하고
         * command 함수(메뉴 클릭)가 호출될 때 이 itemValue 를 전달하기 위해
         * MenuItem 객체에 강제로 주입된 속성이다.
         */
        const itemValue = e.item && (e.item as {itemValue}).itemValue;
        if (itemValue) {
            this.emitFormState(itemValue);
        }
    }

    emitFormState(value: string): void {
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfAccordionMenu)
    static init(): ComponentElement<null> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _newModel: boolean;

    static getDerivedStateFromProps(props, state) {
        const wrappedCompState = state.wrappedCompState;
        /*
         * allExpand, allCollapse은 setProperty를 통해 외부에서 전달받는다.
         * 그러므로 아래의 if절은 setProperty가 호출된 경우에만 유효할 것이다.
         */
        if (wrappedCompState && wrappedCompState.allExpand) {
            const activeItems = BfAccordionMenu._getToAllExpandActiveItem(state.wrappedCompState);
            const activeItemsBeforeSearch = wrappedCompState.isSearched ?
                ObjectHelper.cloneDeep(wrappedCompState.activeItems) : ObjectHelper.cloneDeep(activeItems);
            return {
                wrappedCompState: {
                    ...wrappedCompState,
                    activeItems: activeItems,
                    activeItemsBeforeSearch: activeItemsBeforeSearch,
                    allExpand: false/*초기화*/
                }
            };
        } else if (wrappedCompState && wrappedCompState.allCollapse) {
            return {
                wrappedCompState: {
                    ...wrappedCompState,
                    activeItems: {},
                    activeItemsBeforeSearch: {},
                    allCollapse: false/*초기화*/
                }
            };
        }

        return null;
    }

    private _searchField: HTMLInputElement;
    private _menu: HTMLDivElement;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                multiple: BooleanByTrueFalse.FALSE,
                enableSearch: BooleanByTrueFalse.FALSE,
                model: props.model,
                filteredModel: [],
                activeItems: {},
                lastSelectedItem: {},
                activeItemsBeforeSearch: {}
            }
        };

        this._setSearchFieldRef = this._setSearchFieldRef.bind(this);
        this._setRef = this._setRef.bind(this);
        this._handleKeyPressOnSearch = this._handleKeyPressOnSearch.bind(this);
        this._searchItem = this._searchItem.bind(this);
        this._resetSearch = this._resetSearch.bind(this);
        this._menuAllExpand = this._menuAllExpand.bind(this);
        this._menuAllCollapse = this._menuAllCollapse.bind(this);
        this._setActiveItem = this._setActiveItem.bind(this);
        this._containsItems = this._containsItems.bind(this);
        this._selectedItem = this._selectedItem.bind(this);
    }

    componentDidMount(): void {
        this._expandFirstItem();
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        this._newModel = false;
        this._expandFirstItem();
    }

    private _expandFirstItem(): void {
        const { firstExpand, firstExpanded, activeItems } = this.state.wrappedCompState;
        if (this.state.show && firstExpand && !firstExpanded && activeItems) {
            if (Object.keys(activeItems).length === 0) {
                const menuControlHelper = new MenuControlHelper(this.state.wrappedCompState.model);
                const parentNodes = menuControlHelper.getAllParentNodes();

                if (parentNodes && parentNodes.length > 0) {
                    const toExpandFirst = {[parentNodes[0][ITEMKEY]]: true};
                    this.setState({
                        wrappedCompState: {
                            ...this.state.wrappedCompState,
                            activeItems: toExpandFirst,
                            activeItemsBeforeSearch: ObjectHelper.cloneDeep(toExpandFirst),
                            firstExpanded: true/*초기화*/
                        }
                    });
                }
            } else {
                this.setState({
                    wrappedCompState: {
                        ...this.state.wrappedCompState,
                        firstExpanded: true/*초기화*/
                    }
                });
            }
        }
    }

    componentWillUnmount(): void {
        this._menu = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        if (!this.state.show) {
            return null;
        } else {
            return this._render4Edit();
        }
    }

    private _setSearchFieldRef(ref): void {
        if (ref) {
            this._searchField = ref;
        }
    }

    /**
     * Leaf 노드가 아닌 item을 복수 선택할 수 있도록 item(child를 가지는) 선택 시
     * 해당 item을 activeItems object map에 추가하고 다시 선택(toggle)하면 object map에서 제거한다.
     * @param item
     */
    private _setActiveItem(itemKey: string, itemInfo: ItemInformation): void {
        if (itemKey) {
            const {isSearched, activeItemsBeforeSearch, multiple} = this.state.wrappedCompState;
            let {activeItems, lastSelectedItem} = this.state.wrappedCompState;

            if (itemKey in activeItems) {
                if (itemInfo.hasItemValue) {
                    if (multiple) {
                        lastSelectedItem[itemKey] = !lastSelectedItem[itemKey];
                    } else {
                        // single mode인 경우에는 하나의 아이템만 선택 가능하다.
                        lastSelectedItem = { [itemKey]: true };
                    }

                    if (itemInfo.hasItems) {
                        activeItems[itemKey] = !activeItems[itemKey];
                    }
                } else {
                    activeItems[itemKey] = !activeItems[itemKey];
                }
            } else {
                /*
                if (multiple) {
                    activeItems[itemKey] = true;
                } else {
                    // single mode인 경우에는 하나의 아이템만 선택 가능하다.
                    activeItems = { [itemKey]: true };
                }
                */
                activeItems[itemKey] = true;
                if (itemInfo.hasItemValue) {
                    if (multiple) {
                        lastSelectedItem[itemKey] = true;
                    } else {
                        // single mode인 경우에는 하나의 아이템만 선택 가능하다.
                        lastSelectedItem = { [itemKey]: true };
                    }
                }
            }

            const toStoreActiveItemsBeforeSearch = isSearched ? activeItemsBeforeSearch : ObjectHelper.cloneDeep(activeItems);
            this.setState({
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    activeItems: activeItems,
                    lastSelectedItem: lastSelectedItem,
                    activeItemsBeforeSearch: toStoreActiveItemsBeforeSearch
                }
            });
        }
    }

    private _containsItems(itemKey: string): boolean {
        const activeItems = this.state.wrappedCompState.activeItems;
        return itemKey in activeItems ? activeItems[itemKey] : false;
    }

    private _selectedItem(itemKey: string): boolean {
        const lastSelectedItem = this.state.wrappedCompState.lastSelectedItem;
        return itemKey in lastSelectedItem ? lastSelectedItem[itemKey] : false;
    }

    /**
     * search input에 Enter가 입력될 때 호출되는 핸들러 함수
     * @param event
     * @private
     */
    private _handleKeyPressOnSearch(event): void {
        if (event.key === "Enter") {
            const value = event.target.value;
            if (_.trim(value)) {
                this._filterModel(event.target.value);
            }
        }
    }

    /**
     * 메뉴 아이템 찾기
     * @private
     */
    private _searchItem(): void {
        const value = this._searchField ? this._searchField.value : "";
        if (_.trim(value)) {
            this._filterModel(value);
        }
    }

    private _resetSearch(): void {
        if (this._searchField) {
            this._searchField.value = "";
        }

        if (this.state.wrappedCompState.isSearched) {
            this.setState({
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    filteredModel: ObjectHelper.cloneDeep(this.state.wrappedCompState.model),
                    activeItems: ObjectHelper.cloneDeep(this.state.wrappedCompState.activeItemsBeforeSearch),
                    isSearched: false
                }
            });
        }
    }

    /**
     * 메뉴 펼치기
     * child를 가지는 아이템들만 조회하여 activeItems에 등록한다.
     * @private
     */
    private _menuAllExpand(): void {
        const toExpandAll = BfAccordionMenu._getToAllExpandActiveItem(this.state.wrappedCompState, true);
        this.setState({
            wrappedCompState: {
                ...this.state.wrappedCompState,
                activeItems: toExpandAll,
                activeItemsBeforeSearch: this.state.wrappedCompState.isSearched ?
                                            ObjectHelper.cloneDeep(this.state.wrappedCompState.activeItemsBeforeSearch)
                                            : ObjectHelper.cloneDeep(toExpandAll)
            }
        });
    }

    private static _getToAllExpandActiveItem(wrappedCompState: WrappedCompState, isClickedAllExpandButton: boolean = false): ActiveItem {
        const menuControlHelper = new MenuControlHelper(wrappedCompState.model);
        const parentNodes = menuControlHelper.getAllParentNodes();
        const toExpandAll: ActiveItem = {};
        /*
         * multiple이 true인 경우에만 allExpand 기능을 제공한다.
         * 그렇지 않으면 제일 첫 번째 메뉴만 펼친다.
         * -> Rule 변경: 2019.06.11
         * single 모드라 할지라도 모두 펼쳐준다. 단, 처음 로드 시 allExpand에는 첫번째 메뉴만 펼쳐준다.
         */
        // const parentNodesLength = wrappedCompState.multiple ? parentNodes.length : 1;
        if (parentNodes.length > 0) {
            const parentNodesLength = wrappedCompState.multiple || isClickedAllExpandButton ? parentNodes.length : 1;
            for (let idx = 0; idx < parentNodesLength; ++idx) {
                toExpandAll[parentNodes[idx][ITEMKEY]] = true;
            }
        }

        return toExpandAll;
    }

    /**
     * 메뉴 접기
     * child를 가지는 아이템들만 조회하여 activeItems에서 제거한다.
     * @private
     */
    private _menuAllCollapse(): void {
        this.setState({
            wrappedCompState: {
                ...this.state.wrappedCompState,
                activeItems: {},
                activeItemsBeforeSearch: this.state.wrappedCompState.isSearched ?
                                            ObjectHelper.cloneDeep(this.state.wrappedCompState.activeItemsBeforeSearch)
                                            : {}
            }
        });
    }

    /**
     * search input 창에 입력된 문자열을 포함하는 memu만 필터링한다.
     * @param value
     * @private
     */
    private _filterModel(searchText: string): any {
        const menuControlHelper = new MenuControlHelper(this.state.wrappedCompState.model);
        const filteredMenuItems = menuControlHelper.getFilteredMenuItem(searchText);

        // TODO: method chain 방식으로 개선 필요.
        const menuControlHelperAfterFilter = new MenuControlHelper(filteredMenuItems);
        const parentNodes = menuControlHelperAfterFilter.getAllParentNodes();
        const toExpandAll: ActiveItem = {};
        for (let idx = 0; idx < parentNodes.length; ++idx) {
            toExpandAll[parentNodes[idx][ITEMKEY]] = true;
        }

        this.setState({
            wrappedCompState: {
                ...this.state.wrappedCompState,
                filteredModel: ObjectHelper.cloneDeep(filteredMenuItems),
                activeItems: toExpandAll,
                isSearched: true
            }
        });
    }

    private _render4Edit(): React.ReactNode {
        const w = this.state.wrappedCompState;
        if (this.accessibility) {
            return (
                <AccessibilityMenu
                    {...w}
                    id={this.elementId}
                    key={this.elementId}
                    newModel={this._newModel}
                    ref={this._setRef}
                />
            );
        } else {
            const searchPanel = w.enableSearch ? this._renderSerachPanel() : "";
            return (
                <React.Fragment>
                    <div className="d-flex flex-column h-100">
                        {searchPanel}
                        <AccordionMenu
                            key={w.id}
                            id={w.id}
                            className={w.className}
                            model={w.filteredModel}
                            setActiveItem={this._setActiveItem}
                            containsItems={this._containsItems}
                            selectedItem={this._selectedItem}
                            style={{ flex: "1 1 auto", overflow: "auto" }}
                            ref={this._setRef}
                        />
                    </div>
                </React.Fragment>
            );
        }
    }

    private _renderResetButton(): React.ReactNode {
        return (
            <span className="searchclear mt-1" onClick={this._resetSearch}><i className="fas fa-times-circle"></i></span>
        );
    }

    private _renderSerachPanel(): React.ReactNode {
        const resetButton = this.state.wrappedCompState.isSearched ? this._renderResetButton() : "";
        return (
            <React.Fragment>
                <div className="w-100 d-flex flex-row">
                    <div className="flex-fill position-relative">
                        <input className="searchinput" type="text"
                               onKeyPress={this._handleKeyPressOnSearch}
                               ref={this._setSearchFieldRef}
                               placeholder={this.state.wrappedCompState.placeholder ? this.state.wrappedCompState.placeholder : "Enter keywords..."}
                        />
                        <div className="position-absolute" style={{top: 0, right: 0}}>
                            {resetButton}
                            <span className="searchbtn mt-1" onClick={this._searchItem}><i className="fas fa-search"></i></span>
                        </div>
                    </div>
                    <div style={{flex: "0 0 auto"}}>
                        <div className="floating-btn bf-cursor-pointer" onClick={this._menuAllExpand}><i className="fas fa-angle-double-down"></i></div>
                        <div className="floating-btn bf-cursor-pointer" onClick={this._menuAllCollapse}><i className="fas fa-angle-double-up"></i></div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    private _setRef(ref): void {
        if (ref) {
            this._menu = ref.element;
            this.readyComponent();
        }
    }
}
